/**
 * Kits cache for AI Creations page - cache-first, offline-first policy.
 * Persists kits to localStorage for instant load and offline viewing.
 */

const CACHE_KEY = 'fashdeck_kits_cache';
const CACHE_VERSION = 1;
const MAX_AGE_STALE = 7 * 24 * 60 * 60 * 1000; // 7 days - consider stale but still show when offline
const MAX_KITS_TO_CACHE = 100; // Limit to avoid localStorage quota

/**
 * Get cache key scoped by auth (clear on logout)
 */
function getStorageKey() {
  const token = localStorage.getItem('token');
  if (!token) return null;
  // Use short hash of token to scope cache per user session
  let hash = 0;
  for (let i = 0; i < Math.min(token.length, 32); i++) {
    hash = ((hash << 5) - hash) + token.charCodeAt(i);
    hash |= 0;
  }
  return `${CACHE_KEY}_${Math.abs(hash)}`;
}

/**
 * Read kits from browser cache (localStorage)
 * @returns {{ kits: array, pagination: object, cachedAt: number } | null}
 */
export function getKitsFromCache() {
  try {
    const key = getStorageKey();
    if (!key) return null;

    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.kits) || parsed.version !== CACHE_VERSION) {
      return null;
    }

    return {
      kits: parsed.kits || [],
      pagination: parsed.pagination || { currentPage: 1, hasNextPage: false, totalKits: 0 },
      cachedAt: parsed.cachedAt || 0,
      pageSize: parsed.pageSize || 10
    };
  } catch (e) {
    console.warn('Kits cache read failed:', e);
    return null;
  }
}

/**
 * Persist kits to browser cache
 * @param {{ kits: array, pagination: object }} data
 * @param {number} pageSize
 */
export function saveKitsToCache(data, pageSize = 10) {
  try {
    const key = getStorageKey();
    if (!key) return;

    const kits = Array.isArray(data.kits) ? data.kits.slice(0, MAX_KITS_TO_CACHE) : [];
    const pagination = data.pagination || { currentPage: 1, hasNextPage: false, totalKits: kits.length };

    const payload = {
      version: CACHE_VERSION,
      kits,
      pagination,
      cachedAt: Date.now(),
      pageSize
    };

    localStorage.setItem(key, JSON.stringify(payload));
  } catch (e) {
    console.warn('Kits cache write failed (quota?):', e);
    if (e.name === 'QuotaExceededError') {
      try {
        // Trim to first page only
        const trimmed = { kits: (data.kits || []).slice(0, 20), pagination: data.pagination };
        saveKitsToCache(trimmed, pageSize);
      } catch (e2) {
        console.warn('Kits cache trim write failed:', e2);
      }
    }
  }
}

/**
 * Merge new page data into existing cache (for pagination)
 */
export function mergeKitsIntoCache(newKits, pagination, pageSize = 10) {
  const existing = getKitsFromCache();
  const prevKits = existing?.kits || [];
  const existingIds = new Set(prevKits.map(k => k._id));
  const merged = [...prevKits];
  for (const k of newKits || []) {
    if (k._id && !existingIds.has(k._id)) {
      merged.push(k);
      existingIds.add(k._id);
    }
  }
  // Keep under limit
  const trimmed = merged.slice(0, MAX_KITS_TO_CACHE);
  saveKitsToCache({ kits: trimmed, pagination }, pageSize);
}

/**
 * Clear kits cache (call on logout)
 */
export function clearKitsCache() {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(k => {
      if (k.startsWith(CACHE_KEY)) localStorage.removeItem(k);
    });
  } catch (e) {
    console.warn('Kits cache clear failed:', e);
  }
}

/**
 * Check if cache is stale (older than 30 min) - for background refresh hint
 */
export function isCacheStale() {
  const cached = getKitsFromCache();
  if (!cached) return true;
  return Date.now() - (cached.cachedAt || 0) > 30 * 60 * 1000;
}
